import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import {
  SOCKET_EVENTS,
  type AgentArtifactSocketEvent,
  type ArtifactPreviewVariant,
} from '@tabbio/types';
import {
  artifactRefetchInterval,
  classifyArtifactError,
  type ArtifactPreviewTheme,
  INITIAL_SIGNED_URL_STATE,
  isSignedUrlExpired,
  signedUrlReducer,
  type SignedUrlEvent,
  type SignedUrlState,
} from '../components/artifacts/artifact-model';
import { ensureValidAccessToken } from '../lib/app-auth';
import { getArtifact, mintPreviewUrl } from '../lib/artifacts-api';
import { QUERY_KEYS } from '../lib/query-keys';
import { getSocket, subscribeSocketLifecycle } from '../lib/socket';
import { settleArtifactEdits } from '../components/artifacts/artifact-edit-activity';
import { invalidateArtifactQueries } from './useArtifactVersions';

/**
 * Signed links live 15 minutes. One is reused for 10 so a card and the viewer
 * opened from it share a link, and never handed out close to its expiry.
 */
const SIGNED_URL_REUSE_MS = 10 * 60 * 1000;

/**
 * When a render or image job settles, the API nudges the owner with
 * `SOCKET_EVENTS.ARTIFACT` (no URLs, just the id and status). Listen only
 * while something is pending, and follow the socket singleton across
 * auth-driven recreation like the Nova run watcher does.
 */
export function useArtifactSocketNudge(
  artifactId: string | null | undefined,
  enabled: boolean,
  onNudge: (event: AgentArtifactSocketEvent) => void,
) {
  const onNudgeRef = useRef(onNudge);
  onNudgeRef.current = onNudge;
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    setSocket(getSocket());
    return subscribeSocketLifecycle(setSocket);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !artifactId || !socket) return;
    let active = true;
    const handler = (event: AgentArtifactSocketEvent) => {
      if (event?.artifactId === artifactId) onNudgeRef.current(event);
    };
    socket.on(SOCKET_EVENTS.ARTIFACT, handler);
    // The singleton connects lazily; a pending card is reason enough.
    if (!socket.connected) {
      void ensureValidAccessToken().then((token) => {
        if (active && token && !socket.connected) socket.connect();
      });
    }
    return () => {
      active = false;
      socket.off(SOCKET_EVENTS.ARTIFACT, handler);
    };
  }, [artifactId, enabled, socket]);
}

/**
 * Every `artifact:update` nudge of the signed-in user, for every file (user
 * test fix, 2026-09-25): a ready file changes too (an agent edit, a restore,
 * an image version settling), and the pending-only nudge above never heard
 * those, so an open panel kept the old version until a refresh. Mounted once
 * by the web shell and the native app layout. It reads the file again
 * (detail, versions, checks, site) and ends the agent edits whose version
 * became the head.
 */
export function useArtifactUpdateSync(enabled = true) {
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    setSocket(getSocket());
    return subscribeSocketLifecycle(setSocket);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !socket) return;
    const handler = (event: AgentArtifactSocketEvent) => {
      if (!event || typeof event.artifactId !== 'string' || !event.artifactId) return;
      invalidateArtifactQueries(queryClient, event.artifactId);
      if (event.versionId || typeof event.versionNumber === 'number') {
        settleArtifactEdits(event.artifactId, { id: event.versionId ?? null, number: event.versionNumber ?? null });
      }
    };
    socket.on(SOCKET_EVENTS.ARTIFACT, handler);
    return () => {
      socket.off(SOCKET_EVENTS.ARTIFACT, handler);
    };
  }, [enabled, queryClient, socket]);
}

/**
 * One artifact from `GET /api/artifacts/:id`. While it is pending it refreshes
 * on the socket nudge and, as the fallback, every 5 seconds until it settles.
 * A missing artifact (deleted, or not this user's) is not retried.
 */
export function useArtifactDetail(
  artifactId: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = Boolean(artifactId) && (options.enabled ?? true);
  const query = useQuery({
    queryKey: QUERY_KEYS.ARTIFACT(artifactId ?? ''),
    queryFn: () => getArtifact(artifactId as string),
    enabled,
    staleTime: 15_000,
    retry: (failureCount, error) =>
      classifyArtifactError(error) === 'other' && failureCount < 2,
    refetchInterval: (current) => artifactRefetchInterval(current.state.data?.status),
  });

  const { refetch } = query;
  const pending = !query.data || query.data.status === 'pending';
  const nudge = useCallback(() => {
    void refetch();
  }, [refetch]);
  useArtifactSocketNudge(artifactId, enabled && pending, nudge);

  return query;
}

/**
 * A preview link for one variant, minted on demand. The first load failure
 * (storage answers an expired link with 403) re-mints once by itself; a
 * second failure reports `expired` so the UI can offer Retry. A different
 * artifact, variant or theme starts with a fresh budget.
 */
export function useArtifactSignedUrl(
  artifactId: string | null | undefined,
  variant: ArtifactPreviewVariant | null,
  enabled = true,
  theme: ArtifactPreviewTheme = 'light',
  options: {
    /** A version other than the head (plan 7.5); null or omitted is the head. */
    versionId?: string | null;
    /**
     * Changes when the head changes (its version id and time), so the link
     * is minted again and the preview reloads after an edit or a restore.
     */
    revision?: string | null;
  } = {},
) {
  const versionId = options.versionId ?? null;
  const revision = versionId ? `v:${versionId}` : options.revision ? `h:${options.revision}` : '';
  const key = `${artifactId ?? ''}:${variant ?? ''}:${theme}:${revision}`;
  const [entry, setEntry] = useState<{ key: string; state: SignedUrlState }>({
    key,
    state: INITIAL_SIGNED_URL_STATE,
  });
  const state = entry.key === key ? entry.state : INITIAL_SIGNED_URL_STATE;

  const dispatch = useCallback(
    (event: SignedUrlEvent) => {
      setEntry((previous) => ({
        key,
        state: signedUrlReducer(
          previous.key === key ? previous.state : INITIAL_SIGNED_URL_STATE,
          event,
        ),
      }));
    },
    [key],
  );

  const active = enabled && Boolean(artifactId && variant);
  const query = useQuery({
    queryKey: QUERY_KEYS.ARTIFACT_SIGNED_URL(
      artifactId ?? '',
      variant ?? '',
      theme,
      state.generation,
      revision,
    ),
    queryFn: () =>
      mintPreviewUrl(artifactId as string, variant as ArtifactPreviewVariant, theme, versionId),
    enabled: active,
    staleTime: SIGNED_URL_REUSE_MS,
    gcTime: SIGNED_URL_REUSE_MS,
    // A new link reloads the preview, so it only changes when asked to.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) =>
      classifyArtifactError(error) === 'other' && failureCount < 1,
  });

  const reportLoadError = useCallback(() => dispatch({ type: 'load-error' }), [dispatch]);
  const retry = useCallback(() => dispatch({ type: 'retry' }), [dispatch]);

  return {
    url: active ? query.data?.url ?? null : null,
    signed: active ? query.data ?? null : null,
    loading: active && query.isLoading,
    error: active ? query.error ?? null : null,
    expired: isSignedUrlExpired(state),
    reportLoadError,
    retry,
  };
}
